// DS2.9
import React, { useMemo } from 'react';
import classnames from 'classnames';
import Upload from '../Icons/24x24/Upload';
import ProgressBar from '../ProgressBar';
import BrandedButton, { BrandedButtonProps } from '../Buttons/BrandedButton';
import { IconSize } from '../Icons/IconWrap';
import Loader from '../Loader';
import s from './UploadItemButton.scss';

export interface Props {
  title?: string;
  subtitle?: string;
  dropReadyTitle: string;
  progressTitle: string;
  errorMessage?: React.ReactNode;
  /** underlying input, either constructed with react-dropzone props, or from useFileUpload */
  fileInput: React.ReactNode;
  /** props from react-dropzone */
  rootProps: any;
  iconType?: 'upload' | 'llama' | 'custom' | false;
  icon?: React.ReactNode;
  isDropReady?: boolean;
  dropWithLlama?: boolean;
  ctaVariant?: BrandedButtonProps['variant'];
  ctaLabel?: string;
  aboveCtaSlot?: React.ReactNode;
  belowCtaSlot?: React.ReactNode;
  progress?: number;
  className?: string;
  dropZoneClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}

const UploadItemButton: React.FC<Props> = ({
  title,
  subtitle,
  dropReadyTitle,
  progressTitle,
  errorMessage,
  iconType = 'upload',
  icon: iconProp,
  isDropReady,
  dropWithLlama = true,
  ctaVariant = 'brand-green',
  ctaLabel,
  aboveCtaSlot,
  belowCtaSlot,
  progress,
  className,
  dropZoneClassName,
  contentClassName,
  titleClassName,
  subtitleClassName,
  fileInput,
  rootProps,
}) => {
  const state = useMemo(() => {
    if (isDropReady) {
      return 'drop';
    }

    if (errorMessage) {
      return 'error';
    }

    if (progress) {
      return 'progress';
    }

    return 'ready';
  }, [errorMessage, isDropReady, progress]);

  let icon: React.ReactNode = null;
  if (iconType === 'custom' && iconProp) {
    icon = iconProp;
  } else if (iconType === 'upload') {
    icon = <Upload size={IconSize.MEDIUM} />;
  } else if (iconType === 'llama') {
    icon = (
      <Loader
        size="big"
        state={state === 'error' ? 'spitting' : 'eating_grass'}
      />
    );
  }

  return (
    <div {...rootProps} className={classnames(s['root'], s[state], className)}>
      <div className={classnames(s['dropzone'], dropZoneClassName)}>
        <div className={classnames(s['content'], contentClassName)}>
          {['error', 'ready'].includes(state) && (
            <>
              {icon}
              {title && (
                <div className={classnames(s['title'], titleClassName)}>
                  {title}
                </div>
              )}
              {subtitle && (
                <div className={classnames(s['subtitle'], subtitleClassName)}>
                  {subtitle}
                </div>
              )}
              {aboveCtaSlot}
              {ctaLabel && (
                <BrandedButton
                  className={s['cta']}
                  size="large"
                  variant={ctaVariant}
                >
                  {ctaLabel}
                </BrandedButton>
              )}
              {belowCtaSlot}
            </>
          )}
          {state === 'progress' && (
            <>
              <Loader size="big" state="eating" />
              <ProgressBar
                progress={progress ?? 0}
                className={s['progress-bar']}
              />
              <div className={s['progress-title']}>{progressTitle}</div>
            </>
          )}
          {state === 'drop' && (
            <>
              {dropWithLlama && <Loader state="static_color" size="big" />}
              <div className={s['drop-title']}>{dropReadyTitle}</div>
            </>
          )}
        </div>
      </div>
      {state === 'error' && (
        <div className={s['error-message']}>{errorMessage}</div>
      )}
      {fileInput}
    </div>
  );
};
UploadItemButton.displayName = 'UploadItemButton';

export default UploadItemButton;
